import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi } from '@/lib/api';
import { clearAdminToken, getAdminToken, setAdminToken } from '@/lib/session';
import AdminLogin from './components/AdminLogin';
import AdminPortal from './components/AdminPortal';

type Phase = 'checking' | 'anonymous' | 'authenticated';

export default function AdminPage() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('checking');
  const [token, setToken] = useState('');

  useEffect(() => {
    let active = true;
    const existing = getAdminToken();
    if (!existing) {
      setPhase('anonymous');
      return () => {
        active = false;
      };
    }

    authApi.me(existing).then((res) => {
      if (!active) return;
      if (res.ok && res.role === 'admin') {
        setToken(existing);
        setPhase('authenticated');
        return;
      }
      clearAdminToken();
      setPhase('anonymous');
    });

    return () => {
      active = false;
    };
  }, []);

  const handleSuccess = (nextToken: string) => {
    setAdminToken(nextToken);
    setToken(nextToken);
    setPhase('authenticated');
  };

  const handleLogout = () => {
    const current = getAdminToken();
    if (current) authApi.logout(current);
    clearAdminToken();
    setToken('');
    setPhase('anonymous');
  };

  if (phase === 'checking') {
    return (
      <div className="min-h-[100vh] w-full flex items-center justify-center bg-background-100">
        <div className="flex items-center gap-2.5 text-[13px] text-foreground-500">
          <span className="flex items-center justify-center w-4 h-4">
            <i className="ri-loader-4-line animate-spin text-base text-primary-600" aria-hidden="true" />
          </span>
          {t('admin.common.loading')}
        </div>
      </div>
    );
  }

  if (phase === 'anonymous') {
    return <AdminLogin onSuccess={handleSuccess} />;
  }

  return <AdminPortal token={token} onLogout={handleLogout} />;
}